import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { http } from '../api/http.js';
import { getTeacherToken } from '../hooks/useTeacherAuth.js';
import { getLesson } from '../constants/lessons.js';
import { ArrowLeftIcon, DownloadIcon, UsersIcon, StarIcon, CheckIcon } from '../components/Icons.jsx';
import '../styles/teacher.css';

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear() + 543} ${pad(d.getHours())}:${pad(d.getMinutes())} น.`;
};

/** The exports sit behind the teacher's token, so they are fetched rather than
 *  linked, then handed to the browser as a download. */
async function downloadCsv(path, fallbackName) {
  const base = import.meta.env.VITE_SERVER_URL || '';
  const res = await fetch(`${base}${path}`, {
    credentials: 'include',
    headers: { Authorization: `Bearer ${getTeacherToken()}` },
  });
  if (!res.ok) throw new Error('download failed');
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = match ? match[1] : fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function TeacherHistory() {
  const navigate = useNavigate();
  // ?session=<id> opens one straight away, so a session can be linked to
  const [params, setParams] = useSearchParams();
  const [sessions, setSessions] = useState(null);
  const [openId, setOpenId] = useState(Number(params.get('session')) || null);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!getTeacherToken()) {
      navigate('/teacher', { replace: true });
      return;
    }
    http
      .get('/api/history/sessions')
      .then((res) => setSessions(res.sessions))
      .catch(() => navigate('/teacher', { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    setDetail(null);
    http
      .get(`/api/history/sessions/${openId}`)
      .then(setDetail)
      .catch(() => setError('เปิดรายชื่อคาบนี้ไม่สำเร็จ'));
  }, [openId]);

  const open = (session) => {
    const next = openId === session.id ? null : session.id;
    setOpenId(next);
    setParams(next ? { session: String(next) } : {}, { replace: true });
  };

  return (
    <div className="page">
      <div className="page-wide stack">
        <div className="row-between">
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/teacher')}>
            <ArrowLeftIcon size={14} />
            <span>กลับไปหน้าห้องเรียน</span>
          </button>
        </div>

        <div className="card stack">
          <div>
            <h1>ประวัติการเข้าเรียน</h1>
            <p className="text-dim">
              ทุกครั้งที่สอนจะถูกบันทึกเป็นหนึ่งคาบ พร้อมรายชื่อ คะแนน และคำตอบของคาบนั้น
              กดที่คาบเพื่อดูรายชื่อ หรือดาวน์โหลดเป็นไฟล์ CSV เปิดใน Excel ได้
            </p>
          </div>

          {error && <p className="teacher-error">{error}</p>}

          {sessions === null && <p className="text-dim">กำลังโหลด...</p>}

          {sessions?.length === 0 && (
            <div className="text-center stack" style={{ padding: 'var(--space-4) 0' }}>
              <p className="text-dim">ยังไม่มีคาบเรียนที่บันทึกไว้</p>
            </div>
          )}

          {sessions?.map((s) => {
            const lesson = getLesson(s.roomCode);
            const isOpen = openId === s.id;
            return (
              <div key={s.id} className={`history-session ${isOpen ? 'is-open' : ''}`}>
                <button className="history-session-head" onClick={() => open(s)}>
                  <span className="history-session-when">
                    <strong>{fmtDate(s.startedAt)}</strong>
                    <span className="text-dim">
                      {lesson ? `${lesson.unit} · ${lesson.title}` : `ห้อง ${s.roomCode}`}
                    </span>
                  </span>
                  <span className="history-session-stats">
                    <span className="history-chip"><UsersIcon size={14} /> {s.learners} คน</span>
                    <span className="history-chip">{s.roomCode}</span>
                    <span className={`status-pill ${s.endedAt ? 'is-offline' : 'is-online'}`}>
                      {s.endedAt ? 'จบคาบแล้ว' : 'กำลังเรียน'}
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="history-session-body stack">
                    <div className="row" style={{ gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => downloadCsv(`/api/history/sessions/${s.id}/roster.csv`, 'roster.csv')}
                      >
                        <DownloadIcon size={14} />
                        <span>รายชื่อ + คะแนน (CSV)</span>
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => downloadCsv(`/api/history/sessions/${s.id}/answers.csv`, 'answers.csv')}
                      >
                        <DownloadIcon size={14} />
                        <span>คำตอบรายข้อ (CSV)</span>
                      </button>
                    </div>

                    {detail === null ? (
                      <p className="text-dim">กำลังโหลดรายชื่อ...</p>
                    ) : (
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>ชื่อเล่น</th>
                            <th>เข้าห้อง</th>
                            <th>คะแนน</th>
                            <th>ดาว</th>
                            <th>ตอบถูก</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detail.roster.map((p) => (
                            <tr key={p.id}>
                              <td>
                                <span className="history-name">{p.nickname}</span>
                                {Number(p.alsoSeenInOtherSessions) > 0 && (
                                  <span
                                    className="history-seen-chip"
                                    title="ชื่อนี้เคยถูกใช้ในคาบอื่นด้วย — อาจเป็นคนเดียวกัน ระบบไม่ได้ยืนยันให้"
                                  >
                                    เคยใช้ชื่อนี้อีก {p.alsoSeenInOtherSessions} คาบ
                                  </span>
                                )}
                              </td>
                              <td className="num">{fmtDate(p.joinedAt).split(' ')[1]}</td>
                              <td className="num">{p.score}</td>
                              <td className="num">
                                {Number(p.stars) > 0 ? (
                                  <span className="row" style={{ gap: '4px' }}>
                                    <StarIcon size={12} />
                                    {p.stars}
                                  </span>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="num">
                                <CheckIcon size={12} /> {Number(p.correct || 0)}/{Number(p.answered || 0)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
